import React, { useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    FiArrowRight, FiSearch, FiInfo, FiRefreshCw, FiCheck, FiX, FiDownload,
    FiPlus, FiSend, FiUpload, FiLink, FiUsers
} from 'react-icons/fi';
import { useGroupMembers } from '../hooks/useGroupMembers';
import { useGroupExport } from '../hooks/useGroupExport';
import { useGroupNotifications } from '../hooks/useGroupNotifications';
import { useSettings } from '../hooks/useSettings';
import { useRejectionReasons } from '../hooks/useRejectionReasons';
import '../styles/groupDetail.scss';

const STATUS_OPTIONS = [
    { value: '', label: 'الكل' },
    { value: 'pending', label: 'معلق' },
    { value: 'accepted', label: 'مقبول' },
    { value: 'rejected', label: 'مرفوض' }
];

const STATUS_LABELS = { pending: 'معلق', accepted: 'مقبول', rejected: 'مرفوض' };

const DEFAULT_FILTER_DRAFT = { city: '', referenceNumber: 'APP-', phoneNumber: '', status: '' };
const EMPTY_FILTER_CRITERIA = { city: [], referenceNumber: '', phoneNumber: '', status: '', minAge: '', maxAge: '' };
const EMPTY_ADD_FILTER_CRITERIA = { ...EMPTY_FILTER_CRITERIA, limit: '' };

const calculateAge = (birthDate) => {
    if (!birthDate) return '-';
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    return age;
};

export const GroupDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const {
        group,
        isLoadingGroup,
        members,
        isLoadingMembers,
        currentPage,
        totalPages,
        total,
        fetchMembers,
        applyFilters,
        clearFilters,
        addByReferenceNumbers,
        isAddingByReference,
        addByPhoneNumbers,
        isAddingByPhone,
        previewByFilters,
        isPreviewingByFilter,
        addByFilters,
        isAddingByFilter,
        updateMemberStatus,
        isUpdatingStatus
    } = useGroupMembers(id);

    const { isExporting, progress: exportProgress, exportMembers } = useGroupExport(id);
    const {
        phoneNumbers,
        isLoadingRecipients,
        isSending,
        hasSearched,
        fetchRecipients,
        resetRecipients,
        sendNotification
    } = useGroupNotifications(id);

    const { content: settingsContent } = useSettings();
    const { reasons } = useRejectionReasons();
    const cities = settingsContent.cities || [];

    const [filterDraft, setFilterDraft] = useState(DEFAULT_FILTER_DRAFT);
    const [selectedMember, setSelectedMember] = useState(null);
    const [rejectionDraft, setRejectionDraft] = useState('');
    const [isRejecting, setIsRejecting] = useState(false);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [addMode, setAddMode] = useState('reference');
    const [referenceInput, setReferenceInput] = useState('');
    const [addFilterDraft, setAddFilterDraft] = useState(EMPTY_ADD_FILTER_CRITERIA);
    const [filterPreview, setFilterPreview] = useState(null);
    const [addResult, setAddResult] = useState(null);
    const fileInputRef = useRef(null);

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportFilters, setExportFilters] = useState(EMPTY_FILTER_CRITERIA);
    const [exportFormat, setExportFormat] = useState('excel');
    const [exportFileCount, setExportFileCount] = useState(1);
    const [exportReferenceOnly, setExportReferenceOnly] = useState(false);

    const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
    const [notifyFilterDraft, setNotifyFilterDraft] = useState(EMPTY_FILTER_CRITERIA);
    const [message, setMessage] = useState('');
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const messageRef = useRef(null);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilterDraft((prev) => ({ ...prev, [name]: value }));
    };

    const handleApplyFilters = (e) => {
        e.preventDefault();
        applyFilters(filterDraft);
    };

    const handleClearFilters = () => {
        setFilterDraft(DEFAULT_FILTER_DRAFT);
        clearFilters();
    };

    const openDetails = (member) => {
        setSelectedMember(member);
        setRejectionDraft(member.rejectionReason || '');
        setIsRejecting(false);
    };

    const closeDetails = () => {
        setSelectedMember(null);
        setRejectionDraft('');
        setIsRejecting(false);
    };

    const handleAccept = async () => {
        const updated = await updateMemberStatus(selectedMember._id, { status: 'accepted' });
        if (updated) closeDetails();
    };

    const handleConfirmReject = async () => {
        if (!rejectionDraft.trim()) return;
        const updated = await updateMemberStatus(selectedMember._id, {
            status: 'rejected',
            rejectionReason: rejectionDraft
        });
        if (updated) closeDetails();
    };

    const openAddModal = () => {
        setAddMode('reference');
        setReferenceInput('');
        setAddFilterDraft(EMPTY_ADD_FILTER_CRITERIA);
        setFilterPreview(null);
        setAddResult(null);
        setIsAddModalOpen(true);
    };

    const closeAddModal = () => {
        if (isAddingByReference || isAddingByPhone || isAddingByFilter || isPreviewingByFilter) return;
        setIsAddModalOpen(false);
    };

    const handleAddByReference = async (e) => {
        e.preventDefault();
        const referenceNumbers = referenceInput.split(/[\s,]+/).map((v) => v.trim()).filter(Boolean);
        const result = await addByReferenceNumbers(referenceNumbers);
        if (result) {
            setAddResult(result);
            setReferenceInput('');
        }
    };

    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const referenceNumbers = rows
                .map((row) => (row[0] ?? '').toString().trim())
                .filter((value) => /^APP-\d+$/i.test(value));

            const result = await addByReferenceNumbers(referenceNumbers, true);
            if (result) setAddResult(result);
        } catch (err) {
            setAddResult({ addedCount: 0, skipped: [], error: 'تعذر قراءة الملف، تأكد من أنه ملف Excel صالح' });
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleExcelPhoneUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const phoneNumbers = rows
                .map((row) => (row[0] ?? '').toString().trim())
                .filter((value) => /\d{7,}/.test(value));

            const result = await addByPhoneNumbers(phoneNumbers, true);
            if (result) setAddResult(result);
        } catch (err) {
            setAddResult({ addedCount: 0, skipped: [], error: 'تعذر قراءة الملف، تأكد من أنه ملف Excel صالح' });
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Changing any filter invalidates whatever preview was shown before —
    // otherwise the admin could confirm a batch that no longer matches
    // what's on screen.
    const handleAddFilterChange = (e) => {
        const { name, value } = e.target;
        setAddFilterDraft((prev) => ({ ...prev, [name]: value }));
        setFilterPreview(null);
    };

    const toggleAddFilterCity = (city) => {
        setAddFilterDraft((prev) => ({
            ...prev,
            city: prev.city.includes(city) ? prev.city.filter((c) => c !== city) : [...prev.city, city]
        }));
        setFilterPreview(null);
    };

    const toggleAllAddFilterCities = () => {
        setAddFilterDraft((prev) => ({
            ...prev,
            city: prev.city.length === cities.length ? [] : [...cities]
        }));
        setFilterPreview(null);
    };

    const handlePreviewByFilter = async (e) => {
        e.preventDefault();
        const preview = await previewByFilters(addFilterDraft);
        if (preview) setFilterPreview(preview);
    };

    const handleConfirmAddByFilter = async () => {
        const success = await addByFilters(addFilterDraft);
        if (success) {
            setAddFilterDraft(EMPTY_ADD_FILTER_CRITERIA);
            setFilterPreview(null);
        }
    };

    const openExportModal = () => {
        setExportFilters(EMPTY_FILTER_CRITERIA);
        setExportFormat('excel');
        setExportFileCount(1);
        setExportReferenceOnly(false);
        setIsExportModalOpen(true);
    };

    const closeExportModal = () => {
        if (isExporting) return;
        setIsExportModalOpen(false);
    };

    const handleExportFilterChange = (e) => {
        const { name, value } = e.target;
        setExportFilters((prev) => ({ ...prev, [name]: value }));
    };

    const toggleExportCity = (city) => {
        setExportFilters((prev) => ({
            ...prev,
            city: prev.city.includes(city) ? prev.city.filter((c) => c !== city) : [...prev.city, city]
        }));
    };

    const toggleAllExportCities = () => {
        setExportFilters((prev) => ({
            ...prev,
            city: prev.city.length === cities.length ? [] : [...cities]
        }));
    };

    const handleExportSubmit = async (e) => {
        e.preventDefault();
        const done = await exportMembers(exportFilters, exportFormat, exportFileCount, exportReferenceOnly);
        if (done) setIsExportModalOpen(false);
    };

    const openNotifyModal = () => {
        setNotifyFilterDraft(EMPTY_FILTER_CRITERIA);
        resetRecipients();
        setMessage('');
        setShowLinkInput(false);
        setLinkUrl('');
        setIsNotifyModalOpen(true);
    };

    const handleNotifyFilterChange = (e) => {
        const { name, value } = e.target;
        setNotifyFilterDraft((prev) => ({ ...prev, [name]: value }));
    };

    const toggleNotifyCity = (city) => {
        setNotifyFilterDraft((prev) => ({
            ...prev,
            city: prev.city.includes(city) ? prev.city.filter((c) => c !== city) : [...prev.city, city]
        }));
    };

    const handleNotifySearch = (e) => {
        e.preventDefault();
        fetchRecipients(notifyFilterDraft);
    };

    const handleNotifySend = async (e) => {
        e.preventDefault();
        const sent = await sendNotification(message, notifyFilterDraft);
        if (sent) setIsNotifyModalOpen(false);
    };

    const insertLink = () => {
        const url = linkUrl.trim();
        if (!url) return;

        const textarea = messageRef.current;
        const start = textarea?.selectionStart ?? message.length;
        const end = textarea?.selectionEnd ?? message.length;
        const nextMessage = message.slice(0, start) + url + message.slice(end);

        setMessage(nextMessage);
        setLinkUrl('');
        setShowLinkInput(false);

        requestAnimationFrame(() => {
            textarea?.focus();
            const cursorPos = start + url.length;
            textarea?.setSelectionRange(cursorPos, cursorPos);
        });
    };

    if (isLoadingGroup || !group) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <section className="group-detail-page">
            <div className="page-header">
                <button type="button" className="back-btn" onClick={() => navigate('/groups')}>
                    <FiArrowRight /> رجوع للمجموعات
                </button>
            </div>

            <div className="group-summary">
                <h1>المجموعة {group.number}</h1>
                {group.description && <p className="group-summary__description">{group.description}</p>}
                <div className="group-summary__count"><FiUsers /> {group.memberCount} عضو</div>
            </div>

            <div className="actions-bar">
                <button type="button" className="add-btn" onClick={openAddModal}>
                    <FiPlus /> إضافة أعضاء
                </button>
                <button type="button" className="export-btn" onClick={openExportModal}>
                    <FiDownload /> استخراج
                </button>
                <button type="button" className="notify-btn" onClick={openNotifyModal}>
                    <FiSend /> إرسال إشعار
                </button>
            </div>

            <form className="filters-bar" onSubmit={handleApplyFilters}>
                <div className="filter-field">
                    <label>رقم الهاتف</label>
                    <input
                        type="text"
                        name="phoneNumber"
                        placeholder="بحث برقم الهاتف..."
                        value={filterDraft.phoneNumber}
                        onChange={handleFilterChange}
                    />
                </div>
                <div className="filter-field">
                    <label>الرقم المرجعي</label>
                    <input
                        type="text"
                        name="referenceNumber"
                        placeholder="APP-0123"
                        value={filterDraft.referenceNumber}
                        onChange={handleFilterChange}
                    />
                </div>
                <div className="filter-field">
                    <label>المدينة</label>
                    <select name="city" value={filterDraft.city} onChange={handleFilterChange}>
                        <option value="">الكل</option>
                        {cities.map((city) => (
                            <option key={city} value={city}>{city}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-field">
                    <label>الحالة</label>
                    <select name="status" value={filterDraft.status} onChange={handleFilterChange}>
                        {STATUS_OPTIONS.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-actions">
                    <button type="submit" className="apply-btn">
                        <FiSearch /> تطبيق
                    </button>
                    <button type="button" className="clear-btn" onClick={handleClearFilters}>
                        <FiRefreshCw /> إعادة تعيين
                    </button>
                </div>
            </form>

            {!isLoadingMembers && (
                <div className="results-count">
                    عدد النتائج: <strong>{total}</strong> طلب
                </div>
            )}

            {isLoadingMembers ? (
                <div className="loading-container inline">
                    <div className="loading-spinner"></div>
                </div>
            ) : (
                <>
                    <div className="members-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>الحالة</th>
                                    <th>الاسم الكامل</th>
                                    <th>الرقم الوطني</th>
                                    <th>رقم الهاتف</th>
                                    <th>المدينة</th>
                                    <th>العمر</th>
                                    <th>الرقم المرجعي</th>
                                    <th>تاريخ التقديم</th>
                                    <th>الإجراءات</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members?.map((member) => (
                                    <tr key={member._id}>
                                        <td>
                                            <span
                                                className={`status-dot ${member.status}`}
                                                title={STATUS_LABELS[member.status] || member.status}
                                            ></span>
                                        </td>
                                        <td>{member.fullName}</td>
                                        <td>{member.nationalNumber}</td>
                                        <td>{member.phoneNumber}</td>
                                        <td>{member.city}</td>
                                        <td>{calculateAge(member.birthDate)}</td>
                                        <td>{member.referenceNumber}</td>
                                        <td>{new Date(member.submittedAt).toLocaleDateString('ar-LY')}</td>
                                        <td>
                                            <button className="info-btn" onClick={() => openDetails(member)}>
                                                <FiInfo /> التفاصيل
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {members?.length === 0 && (
                            <div className="no-results">لا يوجد أعضاء مطابقون</div>
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button onClick={() => fetchMembers(currentPage - 1)} disabled={currentPage === 1}>
                                السابق
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => fetchMembers(i + 1)}
                                    className={currentPage === i + 1 ? 'active' : ''}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button onClick={() => fetchMembers(currentPage + 1)} disabled={currentPage === totalPages}>
                                التالي
                            </button>
                        </div>
                    )}
                </>
            )}

            {selectedMember && (
                <div className="modal">
                    <div className="modal-content member-info-modal">
                        <h2>تفاصيل الطلب</h2>

                        <div className="info-group">
                            <p>
                                <span>الحالة:</span>
                                <span className={`status-dot ${selectedMember.status}`}></span>
                                {STATUS_LABELS[selectedMember.status] || selectedMember.status}
                            </p>
                            <p><span>الاسم الكامل:</span> {selectedMember.fullName}</p>
                            <p><span>الرقم الوطني:</span> {selectedMember.nationalNumber}</p>
                            <p><span>رقم الهاتف:</span> {selectedMember.phoneNumber}</p>
                            <p><span>المدينة:</span> {selectedMember.city}</p>
                            <p><span>تاريخ الميلاد:</span> {new Date(selectedMember.birthDate).toLocaleDateString('ar-LY')}</p>
                            <p><span>العمر:</span> {calculateAge(selectedMember.birthDate)}</p>
                            <p><span>الرقم المرجعي:</span> {selectedMember.referenceNumber}</p>
                            <p><span>تاريخ التقديم:</span> {new Date(selectedMember.submittedAt).toLocaleDateString('ar-LY')}</p>
                            {selectedMember.status === 'rejected' && (
                                <p><span>سبب الرفض:</span> {selectedMember.rejectionReason}</p>
                            )}
                        </div>

                        <div className="decision-section">
                            {!isRejecting ? (
                                <div className="decision-actions">
                                    <button type="button" className="accept-btn" onClick={handleAccept} disabled={isUpdatingStatus}>
                                        <FiCheck /> قبول
                                    </button>
                                    <button type="button" className="reject-btn" onClick={() => setIsRejecting(true)} disabled={isUpdatingStatus}>
                                        <FiX /> رفض
                                    </button>
                                </div>
                            ) : (
                                <div className="rejection-form">
                                    <label>سبب الرفض</label>
                                    <input
                                        type="text"
                                        list="rejection-reasons-list"
                                        value={rejectionDraft}
                                        onChange={(e) => setRejectionDraft(e.target.value)}
                                        placeholder="اكتب سبب الرفض أو اختر من القائمة..."
                                    />
                                    <datalist id="rejection-reasons-list">
                                        {reasons.map((reason) => (
                                            <option key={reason} value={reason} />
                                        ))}
                                    </datalist>
                                    <div className="decision-actions">
                                        <button
                                            type="button"
                                            className="reject-btn"
                                            onClick={handleConfirmReject}
                                            disabled={!rejectionDraft.trim() || isUpdatingStatus}
                                        >
                                            <FiX /> تأكيد الرفض
                                        </button>
                                        <button type="button" className="cancel-btn" onClick={() => setIsRejecting(false)}>
                                            إلغاء
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button className="close-btn" onClick={closeDetails}>
                            إغلاق
                        </button>
                    </div>
                </div>
            )}

            {isAddModalOpen && (
                <div className="modal">
                    <div className="modal-content add-members-modal">
                        <h2>إضافة أعضاء للمجموعة</h2>

                        <div className="add-mode-tabs">
                            <button type="button" className={addMode === 'reference' ? 'active' : ''} onClick={() => setAddMode('reference')}>
                                برقم مرجعي
                            </button>
                            <button type="button" className={addMode === 'filter' ? 'active' : ''} onClick={() => setAddMode('filter')}>
                                بالفلترة
                            </button>
                            <button type="button" className={addMode === 'excel' ? 'active' : ''} onClick={() => setAddMode('excel')}>
                                Excel (رقم مرجعي)
                            </button>
                            <button type="button" className={addMode === 'excelPhone' ? 'active' : ''} onClick={() => setAddMode('excelPhone')}>
                                Excel (رقم هاتف)
                            </button>
                        </div>

                        {addMode === 'reference' && (
                            <form onSubmit={handleAddByReference} className="add-reference-form">
                                <div className="input-group">
                                    <label>الأرقام المرجعية</label>
                                    <p className="hint">أدخل رقماً واحداً أو أكثر، مفصولة بفاصلة أو سطر جديد</p>
                                    <textarea
                                        rows={4}
                                        value={referenceInput}
                                        onChange={(e) => setReferenceInput(e.target.value)}
                                        placeholder="APP-0001, APP-0002..."
                                        disabled={isAddingByReference}
                                    />
                                </div>
                                <button type="submit" className="apply-btn" disabled={!referenceInput.trim() || isAddingByReference}>
                                    {isAddingByReference ? 'جارٍ الإضافة...' : 'إضافة'}
                                </button>
                            </form>
                        )}

                        {addMode === 'filter' && (
                            <form onSubmit={handlePreviewByFilter} className="add-filter-form">
                                <div className="filters-grid">
                                    <div className="filter-field">
                                        <label>عدد الأعضاء المطلوب</label>
                                        <input
                                            type="number"
                                            name="limit"
                                            min="1"
                                            placeholder="اتركه فارغاً لإضافة جميع المطابقين"
                                            value={addFilterDraft.limit}
                                            onChange={handleAddFilterChange}
                                            disabled={isAddingByFilter || isPreviewingByFilter}
                                        />
                                    </div>
                                    <div className="filter-field">
                                        <label>رقم الهاتف</label>
                                        <input
                                            type="text"
                                            name="phoneNumber"
                                            value={addFilterDraft.phoneNumber}
                                            onChange={handleAddFilterChange}
                                            disabled={isAddingByFilter || isPreviewingByFilter}
                                        />
                                    </div>
                                    <div className="filter-field">
                                        <label>الرقم المرجعي</label>
                                        <input
                                            type="text"
                                            name="referenceNumber"
                                            placeholder="APP-0123"
                                            value={addFilterDraft.referenceNumber}
                                            onChange={handleAddFilterChange}
                                            disabled={isAddingByFilter || isPreviewingByFilter}
                                        />
                                    </div>
                                    <div className="filter-field">
                                        <label>الحالة</label>
                                        <select name="status" value={addFilterDraft.status} onChange={handleAddFilterChange} disabled={isAddingByFilter || isPreviewingByFilter}>
                                            {STATUS_OPTIONS.map(({ value, label }) => (
                                                <option key={value} value={value}>{label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="filter-field">
                                        <label>العمر من</label>
                                        <input
                                            type="number"
                                            name="minAge"
                                            min="0"
                                            value={addFilterDraft.minAge}
                                            onChange={handleAddFilterChange}
                                            disabled={isAddingByFilter || isPreviewingByFilter}
                                        />
                                    </div>
                                    <div className="filter-field">
                                        <label>العمر إلى</label>
                                        <input
                                            type="number"
                                            name="maxAge"
                                            min="0"
                                            value={addFilterDraft.maxAge}
                                            onChange={handleAddFilterChange}
                                            disabled={isAddingByFilter || isPreviewingByFilter}
                                        />
                                    </div>
                                </div>

                                <div className="filter-field">
                                    <div className="cities-header">
                                        <label>المدن</label>
                                        <button
                                            type="button"
                                            className="toggle-all-btn"
                                            onClick={toggleAllAddFilterCities}
                                            disabled={isAddingByFilter || isPreviewingByFilter}
                                        >
                                            {addFilterDraft.city.length === cities.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                                        </button>
                                    </div>
                                    <div className="cities-checklist">
                                        {cities.map((city) => (
                                            <label key={city} className="city-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={addFilterDraft.city.includes(city)}
                                                    onChange={() => toggleAddFilterCity(city)}
                                                    disabled={isAddingByFilter || isPreviewingByFilter}
                                                />
                                                {city}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <p className="hint">تُستثنى تلقائياً الطلبات المنتمية لأي مجموعة أخرى، والأقدم رقماً مرجعياً يُضاف أولاً</p>

                                <button type="submit" className="apply-btn" disabled={isAddingByFilter || isPreviewingByFilter}>
                                    {isPreviewingByFilter ? 'جارٍ المعاينة...' : 'معاينة'}
                                </button>

                                {filterPreview && (
                                    <div className="filter-preview">
                                        <p className="filter-preview__summary">
                                            سيتم إضافة <strong>{filterPreview.candidates.length}</strong> من إجمالي <strong>{filterPreview.totalAvailable}</strong> طلب متاح (غير منتمٍ لأي مجموعة)
                                        </p>

                                        {filterPreview.candidates.length === 0 ? (
                                            <p className="hint">لا يوجد طلبات مطابقة لإضافتها</p>
                                        ) : (
                                            <ul className="filter-preview__list">
                                                {filterPreview.candidates.map((application) => (
                                                    <li key={application._id}>
                                                        {application.referenceNumber} — {application.fullName} — {application.phoneNumber}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        <button
                                            type="button"
                                            className="confirm-add-btn"
                                            onClick={handleConfirmAddByFilter}
                                            disabled={filterPreview.candidates.length === 0 || isAddingByFilter}
                                        >
                                            {isAddingByFilter ? 'جارٍ الإضافة...' : `تأكيد إضافة ${filterPreview.candidates.length}`}
                                        </button>
                                    </div>
                                )}
                            </form>
                        )}

                        {addMode === 'excel' && (
                            <div className="add-excel-form">
                                <p className="hint">يجب أن يحتوي الملف على عمود واحد فقط يضم الأرقام المرجعية</p>
                                <p className="hint">الأعضاء الموجودون في مجموعة أخرى سيتم نقلهم إلى هذه المجموعة</p>
                                <label className="upload-box">
                                    <FiUpload />
                                    <span>{isAddingByReference ? 'جارٍ المعالجة...' : 'اختر ملف Excel'}</span>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        ref={fileInputRef}
                                        onChange={handleExcelUpload}
                                        disabled={isAddingByReference}
                                    />
                                </label>
                            </div>
                        )}

                        {addMode === 'excelPhone' && (
                            <div className="add-excel-form">
                                <p className="hint">يجب أن يحتوي الملف على عمود واحد فقط يضم أرقام الهاتف</p>
                                <p className="hint">الأعضاء الموجودون في مجموعة أخرى سيتم نقلهم إلى هذه المجموعة</p>
                                <label className="upload-box">
                                    <FiUpload />
                                    <span>{isAddingByPhone ? 'جارٍ المعالجة...' : 'اختر ملف Excel'}</span>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xls"
                                        ref={fileInputRef}
                                        onChange={handleExcelPhoneUpload}
                                        disabled={isAddingByPhone}
                                    />
                                </label>
                            </div>
                        )}

                        {addResult && (
                            <div className="add-result">
                                {addResult.error ? (
                                    <p className="add-result__error">{addResult.error}</p>
                                ) : (
                                    <>
                                        <p className="add-result__summary">
                                            تمت إضافة <strong>{addResult.addedCount}</strong> عضو
                                            {addResult.movedCount > 0 && <> ونقل <strong>{addResult.movedCount}</strong> من مجموعات أخرى</>}
                                            {addResult.skipped?.length > 0 && <> — تم تخطي <strong>{addResult.skipped.length}</strong></>}
                                        </p>
                                        {addResult.skipped?.length > 0 && (
                                            <ul className="add-result__skipped">
                                                {addResult.skipped.map((item, index) => (
                                                    <li key={item.referenceNumber || item.phoneNumber || index}>
                                                        {item.referenceNumber || item.phoneNumber} — {item.reason}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        <button type="button" className="close-btn" onClick={closeAddModal} disabled={isAddingByReference || isAddingByPhone || isAddingByFilter}>
                            إغلاق
                        </button>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal">
                    <div className="modal-content export-modal">
                        <h2>استخراج أعضاء المجموعة</h2>

                        <form onSubmit={handleExportSubmit}>
                            <div className="filters-grid">
                                <div className="filter-field">
                                    <label>رقم الهاتف</label>
                                    <input
                                        type="text"
                                        name="phoneNumber"
                                        value={exportFilters.phoneNumber}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الرقم المرجعي</label>
                                    <input
                                        type="text"
                                        name="referenceNumber"
                                        placeholder="APP-0123"
                                        value={exportFilters.referenceNumber}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الحالة</label>
                                    <select name="status" value={exportFilters.status} onChange={handleExportFilterChange} disabled={isExporting}>
                                        {STATUS_OPTIONS.map(({ value, label }) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-field">
                                    <label>العمر من</label>
                                    <input
                                        type="number"
                                        name="minAge"
                                        min="0"
                                        value={exportFilters.minAge}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>العمر إلى</label>
                                    <input
                                        type="number"
                                        name="maxAge"
                                        min="0"
                                        value={exportFilters.maxAge}
                                        onChange={handleExportFilterChange}
                                        disabled={isExporting}
                                    />
                                </div>
                            </div>

                            <div className="filter-field">
                                <div className="cities-header">
                                    <label>المدن</label>
                                    <button
                                        type="button"
                                        className="toggle-all-btn"
                                        onClick={toggleAllExportCities}
                                        disabled={isExporting}
                                    >
                                        {exportFilters.city.length === cities.length ? 'إلغاء تحديد الكل' : 'تحديد الكل'}
                                    </button>
                                </div>
                                <div className="cities-checklist">
                                    {cities.map((city) => (
                                        <label key={city} className="city-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={exportFilters.city.includes(city)}
                                                onChange={() => toggleExportCity(city)}
                                                disabled={isExporting}
                                            />
                                            {city}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="export-options">
                                <div className="filter-field">
                                    <label>صيغة الملف</label>
                                    <div className="format-toggle">
                                        <button type="button" className={exportFormat === 'excel' ? 'active' : ''} onClick={() => setExportFormat('excel')} disabled={isExporting}>
                                            Excel
                                        </button>
                                        <button type="button" className={exportFormat === 'pdf' ? 'active' : ''} onClick={() => setExportFormat('pdf')} disabled={isExporting}>
                                            PDF
                                        </button>
                                    </div>
                                </div>

                                <div className="filter-field">
                                    <label>نوع البيانات</label>
                                    <div className="format-toggle">
                                        <button type="button" className={!exportReferenceOnly ? 'active' : ''} onClick={() => setExportReferenceOnly(false)} disabled={isExporting}>
                                            بيانات كاملة
                                        </button>
                                        <button type="button" className={exportReferenceOnly ? 'active' : ''} onClick={() => setExportReferenceOnly(true)} disabled={isExporting}>
                                            الرقم المرجعي فقط
                                        </button>
                                    </div>
                                </div>

                                <div className="filter-field">
                                    <label>عدد الملفات</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={exportFileCount}
                                        onChange={(e) => setExportFileCount(Math.max(1, Number(e.target.value) || 1))}
                                        disabled={isExporting}
                                    />
                                </div>
                            </div>

                            {isExporting && exportProgress.total > 0 && (
                                <div className="export-progress">
                                    <div className="export-progress__bar">
                                        <div
                                            className="export-progress__fill"
                                            style={{ width: `${Math.min(100, Math.round((exportProgress.done / exportProgress.total) * 100))}%` }}
                                        />
                                    </div>
                                    <span>جارٍ إنشاء الملف: {exportProgress.done} / {exportProgress.total}</span>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="submit" className="apply-btn" disabled={isExporting}>
                                    {isExporting ? 'جارٍ الاستخراج...' : 'استخراج'}
                                </button>
                                <button type="button" className="clear-btn" onClick={closeExportModal} disabled={isExporting}>
                                    إلغاء
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isNotifyModalOpen && (
                <div className="modal">
                    <div className="modal-content notify-modal">
                        <h2>إرسال إشعار لأعضاء المجموعة</h2>

                        <form className="filters-form" onSubmit={handleNotifySearch}>
                            <div className="filters-grid">
                                <div className="filter-field">
                                    <label>رقم الهاتف</label>
                                    <input
                                        type="text"
                                        name="phoneNumber"
                                        value={notifyFilterDraft.phoneNumber}
                                        onChange={handleNotifyFilterChange}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الرقم المرجعي</label>
                                    <input
                                        type="text"
                                        name="referenceNumber"
                                        placeholder="APP-0123"
                                        value={notifyFilterDraft.referenceNumber}
                                        onChange={handleNotifyFilterChange}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>الحالة</label>
                                    <select name="status" value={notifyFilterDraft.status} onChange={handleNotifyFilterChange}>
                                        {STATUS_OPTIONS.map(({ value, label }) => (
                                            <option key={value} value={value}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-field">
                                    <label>العمر من</label>
                                    <input
                                        type="number"
                                        name="minAge"
                                        min="0"
                                        value={notifyFilterDraft.minAge}
                                        onChange={handleNotifyFilterChange}
                                    />
                                </div>
                                <div className="filter-field">
                                    <label>العمر إلى</label>
                                    <input
                                        type="number"
                                        name="maxAge"
                                        min="0"
                                        value={notifyFilterDraft.maxAge}
                                        onChange={handleNotifyFilterChange}
                                    />
                                </div>
                            </div>

                            <div className="filter-field">
                                <label>المدن</label>
                                <div className="cities-checklist">
                                    {cities.map((city) => (
                                        <label key={city} className="city-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={notifyFilterDraft.city.includes(city)}
                                                onChange={() => toggleNotifyCity(city)}
                                            />
                                            {city}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="filter-actions">
                                <button type="submit" className="apply-btn" disabled={isLoadingRecipients}>
                                    {isLoadingRecipients ? 'جارٍ البحث...' : 'بحث عن المستلمين'}
                                </button>
                            </div>
                        </form>

                        {hasSearched && (
                            <div className="recipients-panel">
                                <div className="recipients-count">
                                    <FiUsers />
                                    تم العثور على <strong>{phoneNumbers.length}</strong> رقم هاتف مطابق للفلترة
                                </div>

                                <form className="message-form" onSubmit={handleNotifySend}>
                                    <div className="input-group">
                                        <div className="message-label-row">
                                            <label>نص الرسالة</label>
                                            <button
                                                type="button"
                                                className="insert-link-btn"
                                                onClick={() => setShowLinkInput((prev) => !prev)}
                                            >
                                                <FiLink /> إدراج رابط
                                            </button>
                                        </div>

                                        {showLinkInput && (
                                            <div className="link-insert-row">
                                                <input
                                                    type="text"
                                                    dir="ltr"
                                                    placeholder="https://..."
                                                    value={linkUrl}
                                                    onChange={(e) => setLinkUrl(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            insertLink();
                                                        }
                                                    }}
                                                />
                                                <button type="button" onClick={insertLink}>إدراج</button>
                                                <button type="button" onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}>
                                                    إلغاء
                                                </button>
                                            </div>
                                        )}

                                        <textarea
                                            ref={messageRef}
                                            rows={4}
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="اكتب نص الإشعار الذي سيتم إرساله للمستلمين..."
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="send-btn"
                                        disabled={phoneNumbers.length === 0 || !message.trim() || isSending}
                                    >
                                        <FiSend /> {isSending ? 'جارٍ الإرسال...' : 'إرسال الإشعار'}
                                    </button>
                                </form>
                            </div>
                        )}

                        <button type="button" className="close-btn" onClick={() => setIsNotifyModalOpen(false)}>
                            إغلاق
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default GroupDetail;
